import {AccountCircle, Link as LinkIcon} from '@mui/icons-material';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography} from '@mui/material';
import {UserAccount} from '../../__generated__/resolvers-types';
import {firebaseAuth} from '../../setup/firebase';
import {linkEtsyAccount} from '../../util/etsy/linkEtsyAccount';

export interface EtsyAccountLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onAccountLinked: () => void;
  user: UserAccount;
}

export default function EtsyAccountLinkDialog({open, onClose, onAccountLinked, user}: EtsyAccountLinkDialogProps) {
  const handleLinkAccount = async () => {
    const token = await firebaseAuth.currentUser?.getIdToken();
    if (!token) {
      // Token not available, user should refresh or login again
      return;
    }

    linkEtsyAccount(token, () => {
      onAccountLinked();
      onClose();
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <AccountCircle color="primary" sx={{fontSize: 32}} />
          <Typography variant="h6">Welcome to CraftySmile!</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1">
            Hi <strong>{user.displayName}</strong>! 👋
          </Typography>

          <Typography variant="body1">
            To get started with CraftySmile, you&apos;ll need to link your Etsy account first. This allows us to help
            you manage your Etsy listings and grow your business.
          </Typography>

          <Box sx={{bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider'}}>
            <Typography variant="body2" color="text.secondary">
              <strong>What happens when you link your account:</strong>
            </Typography>
            <Stack spacing={1} sx={{mt: 1}}>
              <Typography variant="body2" color="text.secondary">
                • Secure connection to your Etsy shop
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Access to bulk listing management tools
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • SEO optimization features
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Product upload and management capabilities
              </Typography>
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Don&apos;t worry - you can always manage your connected accounts later in the settings.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{p: 3, pt: 1}}>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Maybe Later
        </Button>
        <Button
          onClick={handleLinkAccount}
          color="primary"
          variant="contained"
          startIcon={<LinkIcon />}
          sx={{minWidth: 140}}
        >
          Link Etsy Account
        </Button>
      </DialogActions>
    </Dialog>
  );
}
